import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button, Empty, Input, Select, Space, Table, Tabs, Tag, Typography } from 'antd';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import PmvComponentFormModal from '@/components/pmv/PmvComponentFormModal';
import pmvService, { type PmvComponentRegisterItem } from '@/services/pmv.service';
import { useAuthStore } from '@/store/authStore';

const { Text } = Typography;

const FAMILY_OPTS = [
  { value: 'TYRE', label: 'Tyre' },
  { value: 'BATTERY', label: 'Battery' },
  { value: 'GENERIC_COMPONENT', label: 'Generic' },
];

const STATUS_OPTS = [
  'AVAILABLE',
  'IN_STOCK',
  'INSTALLED',
  'UNDER_RETREAD',
  'UNDER_REPAIR',
  'SCRAPPED',
  'DISPOSED',
].map((v) => ({ value: v, label: v.replace(/_/g, ' ') }));

/**
 * Unified component register — /asset/pmv/components
 * Family tabs give tyre/battery optimized columns (W3.10).
 */
const PmvComponentRegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.is_super_admin ||
      user?.permissions?.includes('pmv.component.manage') ||
      user?.permissions?.includes('pmv.admin') ||
      user?.permissions?.includes('pmv.manage')
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const familyTab = searchParams.get('family') || 'ALL';
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<string | undefined>();
  const [availability, setAvailability] = useState<string | undefined>();
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState<PmvComponentRegisterItem | null>(null);

  const family = familyTab === 'ALL' ? undefined : familyTab;

  const listQ = useQuery({
    queryKey: ['pmv-component-register', page, pageSize, q, status, family, availability],
    queryFn: () =>
      pmvService.listComponents({
        page,
        pageSize,
        q: q || undefined,
        status,
        family,
        installed_only: availability === 'installed' ? true : undefined,
        available_only: availability === 'available' ? true : undefined,
      }),
  });

  const baseColumns = useMemo(
    () => [
      {
        title: 'Component',
        dataIndex: 'component_number',
        key: 'component_number',
        fixed: 'left' as const,
        width: 140,
        render: (v: string, r: PmvComponentRegisterItem) => (
          <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/asset/pmv/components/${r.id}`)}>
            {v}
          </Button>
        ),
      },
      {
        title: 'Family',
        dataIndex: 'family',
        key: 'family',
        width: 100,
        render: (v: string) => <Tag>{v}</Tag>,
      },
      {
        title: 'Specification',
        key: 'spec',
        ellipsis: true,
        render: (_: unknown, r: PmvComponentRegisterItem) =>
          r.specification || [r.brand, r.model].filter(Boolean).join(' ') || r.definition_name || '—',
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 120,
        render: (v: string) => <Tag color={v === 'INSTALLED' ? 'blue' : undefined}>{v}</Tag>,
      },
      {
        title: 'Equipment',
        key: 'eq',
        width: 120,
        render: (_: unknown, r: PmvComponentRegisterItem) =>
          r.pmv_profile_id ? (
            <Link to={`/asset/pmv/${r.pmv_profile_id}`}>{r.current_equipment || 'Unit'}</Link>
          ) : (
            r.current_equipment || '—'
          ),
      },
      {
        title: 'Position',
        dataIndex: 'position_code',
        key: 'position',
        width: 100,
        render: (v: string | null) => v || '—',
      },
      {
        title: 'Condition',
        dataIndex: 'condition_code',
        key: 'condition',
        width: 110,
        render: (v: string | null) => v || '—',
      },
      {
        title: 'Warranty',
        dataIndex: 'warranty',
        key: 'warranty',
        width: 90,
      },
      {
        title: 'Attention',
        key: 'attention',
        width: 160,
        render: (_: unknown, r: PmvComponentRegisterItem) =>
          r.attention?.length ? (
            <Space size={4} wrap>
              {r.attention.slice(0, 2).map((h) => (
                <Tag key={h} color="warning">
                  {h}
                </Tag>
              ))}
            </Space>
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 140,
        fixed: 'right' as const,
        render: (_: unknown, r: PmvComponentRegisterItem) => (
          <Space size={0}>
            <Button type="link" size="small" onClick={() => navigate(`/asset/pmv/components/${r.id}`)}>
              Open
            </Button>
            {canManage ? (
              <Button type="link" size="small" onClick={() => setEditRow(r)}>
                Edit
              </Button>
            ) : null}
          </Space>
        ),
      },
    ],
    [navigate, canManage]
  );

  const tyreExtra = [
    {
      title: 'Tread',
      key: 'tread',
      width: 80,
      render: (_: unknown, r: PmvComponentRegisterItem) =>
        r.tyre?.tread_mm != null ? `${r.tyre.tread_mm} mm` : '—',
    },
    {
      title: 'Pressure',
      key: 'psi',
      width: 90,
      render: (_: unknown, r: PmvComponentRegisterItem) =>
        r.tyre?.pressure_psi != null ? `${r.tyre.pressure_psi}` : '—',
    },
    {
      title: 'Retreads',
      key: 'retread',
      width: 80,
      render: (_: unknown, r: PmvComponentRegisterItem) => r.tyre?.retread_count ?? '—',
    },
  ];

  const batteryExtra = [
    {
      title: 'Voltage',
      key: 'v',
      width: 90,
      render: (_: unknown, r: PmvComponentRegisterItem) =>
        r.battery?.voltage != null ? `${r.battery.voltage} V` : '—',
    },
    {
      title: 'SOH',
      key: 'soh',
      width: 80,
      render: (_: unknown, r: PmvComponentRegisterItem) =>
        r.battery?.soh_pct != null ? `${r.battery.soh_pct}%` : '—',
    },
  ];

  const columns =
    familyTab === 'TYRE'
      ? [...baseColumns.slice(0, 7), ...tyreExtra, ...baseColumns.slice(7)]
      : familyTab === 'BATTERY'
        ? [...baseColumns.slice(0, 7), ...batteryExtra, ...baseColumns.slice(7)]
        : baseColumns;

  return (
    <WorkspaceLayout>
      <EnterprisePageHeader
        documentLabel="Component Register"
        subtitle="Tyres, batteries, and managed components — identity survives install / remove / retread"
        primaryActions={
          <Space wrap>
            {canManage ? (
              <Button type="primary" onClick={() => setCreateOpen(true)}>
                New component
              </Button>
            ) : null}
            <Button onClick={() => navigate('/asset/pmv/components/import')}>Import</Button>
            <Button onClick={() => navigate('/asset/pmv/components/control-tower')}>
              Component Tower
            </Button>
            <Button onClick={() => navigate('/asset/pmv')}>Fleet Register</Button>
          </Space>
        }
      />
      <Tabs
        activeKey={familyTab}
        onChange={(k) => {
          setPage(1);
          const next = new URLSearchParams(searchParams);
          if (k === 'ALL') next.delete('family');
          else next.set('family', k);
          setSearchParams(next);
        }}
        items={[
          { key: 'ALL', label: 'All' },
          { key: 'TYRE', label: 'Tyres' },
          { key: 'BATTERY', label: 'Batteries' },
          { key: 'GENERIC_COMPONENT', label: 'Other' },
        ]}
      />
      <Space wrap style={{ marginBottom: 16, width: '100%' }}>
        <Input.Search
          allowClear
          aria-label="Search components by number, serial, name, or fleet"
          placeholder="Number, serial, name, fleet…"
          style={{ width: '100%', maxWidth: 280, minWidth: 180 }}
          onSearch={(v) => {
            setPage(1);
            setQ(v);
          }}
        />
        <Select
          allowClear
          aria-label="Filter by component status"
          placeholder="Status"
          style={{ width: 160 }}
          options={STATUS_OPTS}
          value={status}
          onChange={(v) => {
            setPage(1);
            setStatus(v);
          }}
        />
        <Select
          allowClear
          aria-label="Filter by availability"
          placeholder="Availability"
          style={{ width: 160 }}
          options={[
            { value: 'installed', label: 'Installed' },
            { value: 'available', label: 'Available / stock' },
          ]}
          value={availability}
          onChange={(v) => {
            setPage(1);
            setAvailability(v);
          }}
        />
        {!family && (
          <Select
            allowClear
            aria-label="Filter by component family"
            placeholder="Family"
            style={{ width: 140 }}
            options={FAMILY_OPTS}
            onChange={() => setPage(1)}
          />
        )}
      </Space>
      <Table
        rowKey="id"
        loading={listQ.isLoading}
        columns={columns}
        dataSource={listQ.data?.items ?? []}
        locale={{
          emptyText: (
            <Empty
              description={
                canManage
                  ? 'No components yet — click New component to register a tyre or battery'
                  : 'No components yet'
              }
            >
              {canManage ? (
                <Button type="primary" onClick={() => setCreateOpen(true)}>
                  New component
                </Button>
              ) : null}
            </Empty>
          ),
        }}
        pagination={{
          current: page,
          pageSize,
          total: listQ.data?.total ?? 0,
          showSizeChanger: true,
          responsive: true,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
        scroll={{ x: 1200 }}
        size="middle"
      />

      <PmvComponentFormModal
        open={createOpen}
        mode="create"
        defaultFamily={familyTab}
        onClose={() => setCreateOpen(false)}
        onSaved={(id) => navigate(`/asset/pmv/components/${id}`)}
      />
      <PmvComponentFormModal
        open={Boolean(editRow)}
        mode="edit"
        edit={
          editRow
            ? {
                id: editRow.id,
                family: editRow.family,
                component_number: editRow.component_number,
                serial_number: null,
                condition_code: editRow.condition_code,
                current_location_type: editRow.location_type,
                current_location_reference: editRow.location_reference,
                is_active: true,
              }
            : null
        }
        onClose={() => setEditRow(null)}
      />
    </WorkspaceLayout>
  );
};

export default PmvComponentRegisterPage;
